interface StaticMapBackgroundProps {
  className?: string
}

export function StaticMapBackground({
  className = '',
}: StaticMapBackgroundProps) {
  // Generate a static map URL using OpenStreetMap tiles rendered as a static image
  // In production, you'd use Mapbox Static Images API with your access token:
  // const mapUrl = `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/${centerLon},${centerLat},${zoom}/${width}x${height}?access_token=${MAPBOX_TOKEN}`
  
  // For this demo, we'll use a gradient background with a subtle pattern
  // that gives a map-like feel without requiring an API key
  
  return (
    <div
      className={`fixed inset-0 -z-10 ${className}`}
      aria-hidden="true"
    >
      {/* Base gradient */}
      <div className="absolute inset-0 bg-linear-to-br from-slate-100 via-slate-50 to-slate-100" />
      
      {/* Map-like pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.15]"
        style={{
          backgroundImage: `
            linear-gradient(to right, #94a3b8 1px, transparent 1px),
            linear-gradient(to bottom, #94a3b8 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />
      
      {/* Radial gradient for depth */}
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at 50% 30%, transparent 0%, rgba(241, 245, 249, 0.8) 70%)`,
        }}
      />
      
      {/* Decorative circles representing map markers */}
      <div className="absolute inset-0 overflow-hidden">
        <MapMarker x="30%" y="25%" size={12} delay={0} />
        <MapMarker x="65%" y="35%" size={10} delay={0.5} />
        <MapMarker x="45%" y="60%" size={14} delay={1} />
        <MapMarker x="20%" y="70%" size={8} delay={1.5} />
        <MapMarker x="75%" y="55%" size={11} delay={2} />
        <MapMarker x="55%" y="20%" size={9} delay={2.5} />
      </div>

      {/* Bottom fade for content legibility */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-surface-secondary to-transparent" />
    </div>
  )
}

interface MapMarkerProps {
  x: string
  y: string
  size: number
  delay: number
}

function MapMarker({ x, y, size, delay }: MapMarkerProps) {
  return (
    <div
      className="absolute animate-pulse"
      style={{
        left: x,
        top: y,
        animationDelay: `${delay}s`,
      }}
    >
      <div
        className="rounded-full bg-brand-primary/30"
        style={{
          width: size * 3,
          height: size * 3,
        }}
      />
      <div
        className="absolute rounded-full bg-brand-primary"
        style={{
          width: size,
          height: size,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />
    </div>
  )
}

// Alternative: Real Mapbox Static Image component
// Uncomment and use when you have a Mapbox access token
/*
interface MapboxStaticMapProps {
  accessToken: string
  centerLat: number
  centerLon: number
  zoom?: number
  width?: number
  height?: number
  style?: string
}

export function MapboxStaticMap({
  accessToken,
  centerLat,
  centerLon,
  zoom = 13,
  width = 800,
  height = 600,
  style = 'mapbox/light-v11',
}: MapboxStaticMapProps) {
  const mapUrl = `https://api.mapbox.com/styles/v1/${style}/static/${centerLon},${centerLat},${zoom}/${width}x${height}@2x?access_token=${accessToken}`

  return (
    <div className="fixed inset-0 -z-10">
      <img
        src={mapUrl}
        alt=""
        className="w-full h-full object-cover"
        loading="eager"
      />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-(--color-surface-secondary) to-transparent" />
    </div>
  )
}
*/
